import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const useBlob = (path) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) { setUrl(null); return undefined; }
    let u = null;
    api.get(path, { responseType: "blob" }).then(({ data }) => { u = URL.createObjectURL(data); setUrl(u); }).catch(() => setUrl(null));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [path]);
  return url;
};

export const imgPath = (n) => (n.has_image ? `/servicedesk/news/${n.slot}/image?v=${n.image_v}` : null);

const Viewer = ({ n, onClose }) => {
  const pdf = useBlob(n?.kind === "pdf" && n.has_pdf ? `/servicedesk/news/${n.slot}/pdf?v=${n.pdf_v}` : null);
  if (!n) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="sd-news-viewer">
        <DialogHeader><DialogTitle data-testid="sd-news-viewer-title">{n.title}</DialogTitle></DialogHeader>
        {n.summary && <p className="text-sm text-slate-500">{n.summary}</p>}
        {n.kind === "pdf" && (pdf ? <iframe title={n.title} src={pdf} className="w-full h-[70vh] rounded-lg border" data-testid="sd-news-pdf" />
          : <p className="text-sm text-slate-400">{n.has_pdf ? "Loading PDF…" : "No PDF attached yet."}</p>)}
        {n.kind === "article" && <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="sd-news-body">{n.body}</div>}
        {n.kind === "link" && (<div className="space-y-3">{n.body && <p className="text-sm whitespace-pre-wrap">{n.body}</p>}
          <Button asChild data-testid="sd-news-link"><a href={n.url} target="_blank" rel="noreferrer"><Icons.ExternalLink className="h-4 w-4 mr-1" />Open link</a></Button></div>)}
        {pdf && <a href={pdf} download={n.pdf_name} className="text-xs text-sky-700 hover:underline" data-testid="sd-news-pdf-download">Download PDF</a>}
      </DialogContent>
    </Dialog>
  );
};

const KIND_ICON = { pdf: Icons.FileText, article: Icons.Newspaper, link: Icons.ExternalLink };

const Tile = ({ n, onOpen }) => {
  const img = useBlob(imgPath(n));
  const Ic = KIND_ICON[n.kind] || Icons.Newspaper;
  return (
    <button type="button" onClick={() => onOpen(n)} data-testid={`sd-news-tile-${n.slot}`}
      className="group text-left bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col hover:border-sky-300 hover:shadow-md transition-[box-shadow,border-color]">
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
        {img ? <img src={img} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          : <div className="w-full h-full grid place-items-center text-slate-300"><Ic className="h-10 w-10" /></div>}
      </div>
      <div className="p-3 flex-1">
        <div className="text-sm font-semibold text-sky-800 group-hover:underline line-clamp-2 flex gap-1.5"><Ic className="h-4 w-4 shrink-0 mt-0.5" />{n.title}</div>
        {n.summary && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{n.summary}</div>}
      </div>
    </button>
  );
};

export const NewsBoard = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/servicedesk/news").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <section data-testid="sd-news-board">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><Icons.Megaphone className="h-4 w-4 text-sky-600" />IT News &amp; Updates</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{rows.map((n) => <Tile key={n.slot} n={n} onOpen={setOpen} />)}</div>
      <Viewer n={open} onClose={() => setOpen(null)} />
    </section>
  );
};
